/**
 * ReplaceSpanEditor.tsx — PLAT-10214 (T6, epic PLAT-10209)
 *
 * The editor popover for span-replace in the Translation Review workspace. A
 * reviewer selects a contiguous run of TRANSLATED words, the run's current
 * translation pre-fills here, they type one correction, and Confirm authors a
 * reversible override segment (D10) via `TranslationStore.createOverrideSegment`
 * (or, when the span sits inside an existing manual/directed segment, updates it
 * via `updateOverrideSegment`; or, when it overlaps existing corrections,
 * absorbs them via `absorbIntoOverrideSegment` — PLAT-11655, silently: the
 * editor shows no line about it). Cancel restores + unlocks.
 *
 * It mirrors the single-phrase `TranslatedPhraseEditorPopover` patterns
 * (anchored `position: fixed`, portaled to `document.body` and placed against
 * its measured height by `useAnchoredPopoverPlacement` — PLAT-11484 /
 * PLAT-11479, see the portal note at the render; native keydown trap so
 * transcript / video shortcuts don't fire while typing; shared
 * translation-field primitives — PLAT-10644) and, like it, can show the span's
 * "Previous translation"
 * (read-only, when it differs from the current text or an override is
 * revertible) with an inline "Revert" action on that row (PLAT-10265).
 * Revert is enabled only when re-editing an EXISTING override: it drops that
 * override and restores the machine fragments. A fresh, not-yet-authored span
 * has no override to revert, so the host disables it via `revertDisabled`.
 * The covered source text sits behind a collapsed "Original" disclosure.
 * Design-system compliant: Title Case button labels, sentence-case helper text,
 * NO uppercased labels, green `--primary` for the save action.
 *
 * A REFUSED span (`blockedMessage` — cross-speaker) does not render this
 * editor at all: it renders the anchored WARNING panel near the top of the
 * component, because there is nothing to type and nothing to save.
 */

import { TOP_LAYER_VARS } from '@src/components/common/topLayerZ';
import { useAnchoredPopoverPlacement } from '@src/components/common/useAnchoredPopoverPlacement';
import { CLIP_PREVIEW_Z } from '@src/components/content-components/clipPreviewZ';
import CircleExclammationIcon from '@src/svgs/CircleExclammationIcon';
import { formatEditedAt } from '@src/utility/dateFormat';
import { normalizeForCompare } from '@src/utility/strings';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

import {
  SPAN_CROSS_SPEAKER_BLOCK_MESSAGE,
  SPAN_MACHINE_RETRANSLATE_UNAVAILABLE_MESSAGE,
} from './hooks/useSpanReplace';
import {
  ButtonRow,
  CollapsibleOriginal,
  CollapsibleValueSection,
  fieldFocusCss,
  InlineTextAction,
  OriginalReference,
  PrimaryButton,
  SecondaryButton,
  SectionLabel,
  Spacer,
} from './translationEditorPrimitives';
import { useOutsideAlerter } from './useClickOutside';
import { useExclusiveTranslationPopover } from './useExclusiveTranslationPopover';

import type {
  ConfirmDirectedFailureReason,
  ConfirmDirectedResult,
} from './hooks/useSpanReplace';
import type { AnchoredPopoverPlacement } from '@src/components/common/useAnchoredPopoverPlacement';

export interface ReplaceSpanEditorProps {
  /** The element to anchor the popover below — the selection's first word. */
  anchorElement: HTMLElement;
  /** The source-language text the span covers (read-only context). */
  sourceText: string;
  /**
   * PLAT-10644 — human-readable source-language name ("Italian") for the
   * collapsed "Original · <language>" disclosure. Omitted → "Original".
   */
  sourceLanguageLabel?: string;
  /** The current translated text of the span (pre-fills the input). */
  currentValue: string;
  /**
   * The machine-original translation of the span — what it said before any
   * override / individual edit. Shown read-only as "Previous translation" when
   * it differs from the current text or an override is revertible (the same
   * comparison the single-phrase popover makes). Undefined when unknown —
   * then the row hides.
   */
  originalValue?: string;
  /** How many source words the span covers — shown in the helper line. */
  wordCount: number;
  /**
   * How many source words the TYPED correction replaces (PLAT-11648). In Segment
   * view the span is widened to the whole segment for the write, but the box now
   * holds only the highlighted words, so "Replacing N words" must count what the
   * reviewer highlighted or it contradicts the box. Defaults to `wordCount`,
   * which is what every un-widened surface wants. Re-translate keeps `wordCount`:
   * it really does send the whole covered span.
   */
  replacingWordCount?: number;
  /** Commit the typed correction. Fires on Confirm or Enter. */
  onConfirm: (value: string) => void;
  /** Close without committing. Fires on Cancel, Escape, or click-outside. */
  onCancel: () => void;
  /**
   * Revert to the machine original (PLAT-10265): the host drops the override
   * segment and restores the machine fragments, then closes. Rendered as an
   * inline "Revert" action on the "Previous translation" row (PLAT-10644).
   */
  onRevert: () => void;
  /**
   * Disables "Revert" when there is nothing applied to revert (a fresh span
   * with no existing override). While disabled the action only shows when
   * the machine translation differs from the draft.
   */
  revertDisabled: boolean;
  /**
   * When set, the span can't be replaced as-is (it crosses a speaker change —
   * PLAT-10945). The component then renders a WARNING PANEL in place of the
   * editor entirely — attention icon, title, this reason, and Close — rather
   * than the editor with its input removed.
   */
  blockedMessage?: string;
  /**
   * The reviewer who last applied this change (PLAT-10444) — the override
   * segment's `editedBy`, or the covered phrase's for a single-phrase fix.
   * Drives the "Last edited by … · …" meta line, shown only when re-editing an
   * existing change. Absent on a fresh span / pre-attribution edits ⇒ the line
   * hides.
   */
  editedBy?: { name: string; email?: string; at?: string };
  /**
   * PLAT-10257 (A3) — directed span re-translate: submit the instruction-mode
   * text (`useSpanReplace.confirmDirected`). Providing this enables the
   * editor's two-mode UI (Type Replacement | Re-translate) behind the single
   * "Fix Translation" entry; absent (the inline hosts), the editor renders
   * exactly the single manual mode. An `ok` result means the host closes the
   * editor; a failure keeps it open — the editor renders the reason inline and
   * preserves the typed instruction so the reviewer can resubmit or switch
   * modes.
   */
  onConfirmDirected?: (instruction: string) => Promise<ConfirmDirectedResult>;
  /**
   * PLAT-10257 (A3, locked decision 2026-07-16) — set when the span is the
   * RE-EDIT case (entirely within one existing fix): manual mode re-edits as
   * today, but instruction mode is unavailable. Switching to it shows this
   * notice (the standard overlap message) instead of the instruction input,
   * with no submit.
   */
  instructionModeBlockedMessage?: string;
  /**
   * PLAT-10266 (2026-07-28) — one-click MACHINE regenerate, with NO instruction to
   * write. Wired only by the STALE-FLAG entry, whose contract is a straight machine
   * re-translation against the corrected source and explicitly NOT a directed one
   * (SPEC: "does NOT take a free-text instruction — that is PLAT-10257"). That entry
   * therefore passes this and withholds `onConfirmDirected`, so the reviewer sees
   * exactly two ways out: type the translation, or regenerate it. Absent everywhere
   * else, so the coverage-popover entry keeps its two-mode instruction UI unchanged.
   */
  onRegenerate?: () => void;
  /**
   * PLAT-10266 (2026-07-28) — why the last machine regenerate was REFUSED, or undefined
   * if it wasn't. Arrives as a prop rather than from local state because the op does not
   * run here: the stale-flag host starts it and a workspace effect awaits it (it needs
   * the OPENED editor's `existingOverrideSegmentId` to pick its branch first). Same
   * reason space and same copy as the directed path's own failure — see
   * `toEditorFailureReason` in `resolveStaleFragment.ts`.
   *
   * Rendered in MANUAL mode, unlike `directedFailure`: that notice sits inside the
   * instruction-mode section, which this entry can never reach (it withholds
   * `onConfirmDirected`). Without a manual-mode placement the reason would be computed
   * and never shown.
   */
  regenerateFailureReason?: ConfirmDirectedFailureReason;
}

/**
 * The blocked panel's heading. A refused span is not an editing task, so the
 * panel states the OUTCOME here and leaves the cause + the remedy to the
 * reason copy underneath (`docs/design-system.md` → "Confirmation Dialogs":
 * never one continuous block of heading text). One title covers both refusals
 * — each reason message already names which one it is — so no host has to
 * pass a second string. Title Case (design system: UI label).
 */
const BLOCKED_PANEL_TITLE = "Can't Fix This Selection";

/**
 * PLAT-10257 (A3) — post-submit blocked reasons from `confirmDirected` map to
 * the standard span notices (the editor's existing blocked idiom); reasons not
 * listed here render the minimal inline error row below (A6 replaces it with
 * the polished notice).
 */
/**
 * Copy for a span whose SOURCE cannot be identified any more (PLAT-10266,
 * 2026-08-04). The machine re-translate refuses rather than guess — anchoring on
 * the stored index instead would re-translate a DIFFERENT word than the reviewer
 * chose (measured: the fragment "обычно" stores index 233, which now lands on the
 * unrelated word "iPhones"), so the refusal is correct and permanent for this span.
 *
 * It is separated from `DIRECTED_ERROR_TEXT` because that copy ends "try again",
 * which is false here: retrying can never succeed. The common cause is a source
 * word corrected in an EARLIER session — the re-translate anchors on a record
 * captured at page load, and by then the edit is already baked into the array — but
 * the copy states only the OBSERVABLE fact, since the same reason covers any span
 * whose identities no longer resolve.
 *
 * The action it names works from right here: this editor already holds the
 * pre-filled input, so the reviewer types over it and saves. Sentence case
 * (design system: prose, not a button label).
 */
export const SPAN_SOURCE_UNIDENTIFIABLE_MESSAGE =
  'The original words this translation came from can no longer be identified, so the machine cannot re-translate it. Type the replacement yourself and save.';

const DIRECTED_BLOCKED_TEXT: Partial<
  Record<ConfirmDirectedFailureReason, string>
> = {
  'blocked-within-fix': SPAN_MACHINE_RETRANSLATE_UNAVAILABLE_MESSAGE,
  'blocked-overlap': SPAN_MACHINE_RETRANSLATE_UNAVAILABLE_MESSAGE,
  // PLAT-10945 — the re-translate mode is a sibling of Save behind the same
  // entry, so it states the SPEAKER reason instead of falling through to the
  // overlap copy (which would name a correction that does not exist).
  'blocked-cross-speaker': SPAN_CROSS_SPEAKER_BLOCK_MESSAGE,
  // PLAT-10266 — a permanent refusal, not a transient error: route it to the
  // reasoned Notice instead of the "try again" row.
  'missing-source-element': SPAN_SOURCE_UNIDENTIFIABLE_MESSAGE,
};

/** Minimal failure copy for backend/author/missing-source failures (A6
 *  replaces this). Nothing was authored or persisted on these paths. */
const DIRECTED_ERROR_TEXT =
  'Re-translating this selection failed. Nothing was changed — try again.';

export const ReplaceSpanEditor: React.FC<ReplaceSpanEditorProps> = ({
  anchorElement,
  sourceText,
  sourceLanguageLabel,
  currentValue,
  originalValue,
  wordCount,
  replacingWordCount,
  onConfirm,
  onCancel,
  onRevert,
  onRegenerate,
  regenerateFailureReason,
  revertDisabled,
  blockedMessage,
  editedBy,
  onConfirmDirected,
  instructionModeBlockedMessage,
}) => {
  const [value, setValue] = useState(currentValue);
  // PLAT-11648 — what the typed correction replaces, which is the highlighted
  // run rather than the widened span the write lands on.
  const replacedCount = replacingWordCount ?? wordCount;
  // PLAT-10257 (A3) — the two-mode state. Manual is the default (today's
  // behavior); the instruction draft and its failure state live here so a
  // failed run keeps the typed text (failure floor). Only meaningful when
  // `onConfirmDirected` is wired — otherwise the mode never leaves 'manual'.
  const [mode, setMode] = useState<'manual' | 'instruction'>('manual');
  const [instruction, setInstruction] = useState('');
  const [isRetranslating, setIsRetranslating] = useState(false);
  const [directedFailure, setDirectedFailure] =
    useState<ConfirmDirectedFailureReason | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const instructionTextareaRef = useRef<HTMLTextAreaElement>(null);
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  // Placed against the RENDERED height, flipping above / clamping into the
  // viewport as needed (PLAT-11479 — a hardcoded estimate left the Save row and
  // the input below the fold whenever the anchor sat low in the panel).
  const placement = useAnchoredPopoverPlacement(anchorElement, wrapperRef, {
    width: POPOVER_WIDTH,
    estimatedHeight: POPOVER_ESTIMATED_HEIGHT,
  });

  useOutsideAlerter(wrapperRef, onCancel);

  // PLAT-10644 — opening any other translation popover cancels this one.
  useExclusiveTranslationPopover(onCancel);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    // Focus + select-all: a span replace is a "retype the whole run" gesture
    // (unlike the single-word editor, which lands the caret at the end to
    // tweak one word). Selecting lets the reviewer overtype the run in one go,
    // while still being able to click to place the caret.
    textarea.focus();
    textarea.setSelectionRange(0, textarea.value.length);
  }, []);

  // Native keydown trap — see `TranslatedPhraseEditorPopover` for the full
  // rationale. The transcript scroll container + video shortcuts listen on
  // ancestors via native `addEventListener`, which React's synthetic
  // `stopPropagation` does not stop. Escape is handled here (cancels the edit,
  // stops propagation) so it never reaches the workspace's own Escape-to-close.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCancelRef.current();
        return;
      }
      if (e.key === 'Enter') return; // let the textarea onKeyDown handle it
      e.stopPropagation();
    };
    const stop = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === 'Escape') return;
      e.stopPropagation();
    };
    wrapper.addEventListener('keydown', onKeyDown);
    wrapper.addEventListener('keyup', stop);
    wrapper.addEventListener('keypress', stop);
    return () => {
      wrapper.removeEventListener('keydown', onKeyDown);
      wrapper.removeEventListener('keyup', stop);
      wrapper.removeEventListener('keypress', stop);
    };
  }, []);

  const trimmed = value.trim();
  // Has the reviewer actually changed the box, whitespace-insensitively? Drives
  // both the Confirm gate and the machine-translation reference row below.
  const draftEdited =
    normalizeForCompare(value) !== normalizeForCompare(currentValue);
  // Require a real change vs. the pre-filled value, so Confirm is disabled on
  // open and on whitespace-only / no-op edits. (Revert is independent — it uses
  // canRevert/revertDisabled, not this gate.)
  const canConfirm = trimmed.length > 0 && draftEdited;
  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    e.stopPropagation();
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleConfirm();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  // PLAT-10257 (A3) — the two-mode UI shows only when the host wires the
  // directed path. A blocked span never reaches here: it returns the warning
  // panel above, which has no modes at all.
  const showModeToggle = !!onConfirmDirected;
  const instructionMode = showModeToggle && mode === 'instruction';
  const switchMode = (next: 'manual' | 'instruction') => {
    setMode(next);
    // A stale failure from the other mode's run would mislead — clear it.
    setDirectedFailure(null);
  };

  // Empty/whitespace-only instructions never reach the backend (AC5 — the same
  // trim gate the directed modal's `canSubmit` uses); the in-flight guard
  // blocks a double-submit double-spend while the backend call runs.
  const canRetranslate =
    instruction.trim().length > 0 &&
    !isRetranslating &&
    !instructionModeBlockedMessage;
  const handleConfirmDirected = async () => {
    if (!onConfirmDirected || !canRetranslate) return;
    setDirectedFailure(null);
    setIsRetranslating(true);
    try {
      const result = await onConfirmDirected(instruction);
      // ok ⇒ the host closes the editor (the hook clears its state only on
      // success); a failure keeps everything intact and renders inline.
      if (!result.ok) setDirectedFailure(result.reason);
    } finally {
      setIsRetranslating(false);
    }
  };

  const handleInstructionKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    e.stopPropagation();
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleConfirmDirected();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  // Focus follows the mode. The mount effect above covers the initial manual
  // render (this pass duplicates it harmlessly); on a switch, instruction mode
  // lands the caret at the end (tweak-and-resubmit), back-to-manual re-selects
  // the run (the same "retype the whole run" gesture as opening).
  useEffect(() => {
    if (mode === 'instruction') {
      const textarea = instructionTextareaRef.current;
      if (!textarea) return;
      textarea.focus();
      const end = textarea.value.length;
      textarea.setSelectionRange(end, end);
    } else {
      const textarea = textareaRef.current;
      if (!textarea) return;
      textarea.focus();
      textarea.setSelectionRange(0, textarea.value.length);
    }
  }, [mode]);

  // Post-submit blocked reasons render via the standard notice; other failures
  // via the minimal error row (see DIRECTED_BLOCKED_TEXT / A6).
  const directedBlockedText = directedFailure
    ? DIRECTED_BLOCKED_TEXT[directedFailure]
    : undefined;

  // PLAT-10266 (2026-07-28) — the machine regenerate's refusal, through the SAME copy
  // map. A reason the map covers reads as the standard block notice; anything else (a
  // backend/author/missing-source failure) falls to the same minimal error copy the
  // directed path uses. Before this the reason was discarded and the click looked dead.
  const regenerateBlockedText = regenerateFailureReason
    ? DIRECTED_BLOCKED_TEXT[regenerateFailureReason]
    : undefined;

  // A refused span is not an editor: there is nothing to type and nothing to
  // save, so it renders as a WARNING panel — attention icon, the outcome as a
  // title, the reason + remedy under it, and the way out — rather than the Fix
  // Translation chrome with its input and Save deleted (which read as an
  // editor that had gone inert). Same anchored placement and same test ids:
  // only the presentation differs. Amber `--accent` attention tint, built the
  // way `CaptionStalenessNotice` builds it (`--warning` is not a theme token).
  if (blockedMessage) {
    return ReactDOM.createPortal(
      <Wrapper
        ref={wrapperRef}
        placement={placement}
        data-testid="replace-span-editor"
        onMouseDown={e => e.stopPropagation()}
        role="alertdialog"
        aria-label={BLOCKED_PANEL_TITLE}
      >
        <WarningHeader>
          <WarningIcon>
            <CircleExclammationIcon
              width="16"
              height="16"
              strokeColor="var(--accent)"
            />
          </WarningIcon>
          <WarningTitle>{BLOCKED_PANEL_TITLE}</WarningTitle>
        </WarningHeader>
        <Notice role="alert" data-testid="replace-span-blocked">
          {blockedMessage}
        </Notice>
        <ButtonRow>
          <Spacer />
          <SecondaryButton
            type="button"
            onClick={onCancel}
            data-testid="replace-span-cancel"
          >
            Close
          </SecondaryButton>
        </ButtonRow>
      </Wrapper>,
      document.body,
    );
  }

  // Portaled to `document.body` (PLAT-11484). Rendered inline, the editor is a
  // DOM descendant of the transcript scroll container, and in the clip preview
  // the modal wrapper's `backdrop-filter` makes `position: fixed` resolve
  // against the modal rather than the viewport — so Chrome's drag-to-select
  // autoscroll, which walks up from the textarea to the nearest scrollable
  // ancestor, found the transcript and scrolled it whenever a selection drag
  // left the textarea. Body-level, that walk never reaches the transcript.
  return ReactDOM.createPortal(
    <Wrapper
      ref={wrapperRef}
      placement={placement}
      data-testid="replace-span-editor"
      onMouseDown={e => e.stopPropagation()}
      role="dialog"
      aria-label="Fix translation"
    >
      {/* Expanded by default — the source text is the reviewer's reference
          for judging the translation, so it opens revealed (PLAT-10702). */}
      <CollapsibleOriginal
        label={
          sourceLanguageLabel ? `Original · ${sourceLanguageLabel}` : 'Original'
        }
        text={sourceText}
        testId="replace-span-source-text"
        defaultExpanded
      />

      {/*
        Machine-translation reference + its Revert action share one row
        (PLAT-10644): Revert restores THIS value, so the affordance sits
        next to what it restores rather than in the modal footer. The
        `!revertDisabled` term keeps the row (and Revert) visible while
        re-editing an existing override even when the draft matches the
        machine value. An untouched span shows neither — the permanently-
        disabled footer Revert it replaced was noise.

        The gate is "has the reviewer TYPED something" (`draftEdited`), not
        "does the machine text differ from the box" (pull request #3566
        review). Since PLAT-11648 the box holds only the highlighted words
        while `originalValue` is the whole covered run's machine text, so on a
        freshly widened Segment-view span those two can never be equal and the
        row drew on every open, carrying a Revert that could never be enabled —
        exactly the noise PLAT-10644 removed.
      */}
      {originalValue !== undefined && (draftEdited || !revertDisabled) && (
        <CollapsibleValueSection
          label="Original Translation"
          testId="replace-span-original-translation"
          action={
            <InlineTextAction
              type="button"
              onClick={onRevert}
              disabled={revertDisabled}
              data-testid="replace-span-revert"
            >
              Revert
            </InlineTextAction>
          }
        >
          <OriginalReference data-testid="replace-span-original-translation">
            {originalValue}
          </OriginalReference>
        </CollapsibleValueSection>
      )}

      {/* PLAT-10257 (A3) — the two modes behind the single "Fix Translation"
          entry (locked decision: no second menu item). Rendered only when the
          host wires the directed path. */}
      {showModeToggle && (
        <ModeToggle
          role="group"
          aria-label="Fix mode"
          data-testid="replace-span-mode-toggle"
        >
          <ModeButton
            type="button"
            $active={mode === 'manual'}
            aria-pressed={mode === 'manual'}
            onClick={() => switchMode('manual')}
            data-testid="replace-span-mode-manual"
          >
            Type Replacement
          </ModeButton>
          <ModeButton
            type="button"
            $active={mode === 'instruction'}
            aria-pressed={mode === 'instruction'}
            onClick={() => switchMode('instruction')}
            data-testid="replace-span-mode-instruction"
          >
            Re-translate
          </ModeButton>
        </ModeToggle>
      )}

      {instructionMode ? (
        instructionModeBlockedMessage ? (
          // The RE-EDIT (within-fix) case: instruction mode is unavailable
          // (locked decision 2) — the standard overlap notice explains why;
          // manual mode keeps working as today.
          <Section>
            <Notice role="alert" data-testid="replace-span-instruction-blocked">
              {instructionModeBlockedMessage}
            </Notice>
          </Section>
        ) : (
          <Section>
            <SectionLabel>Instruction</SectionLabel>
            <Textarea
              ref={instructionTextareaRef}
              value={instruction}
              disabled={isRetranslating}
              placeholder="e.g. Use a more formal tone; keep product names in English."
              onChange={e => {
                setInstruction(e.target.value);
                // Editing invalidates the previous run's failure message.
                setDirectedFailure(null);
              }}
              onKeyDown={handleInstructionKeyDown}
              rows={2}
              aria-label="Re-translate instruction"
              data-testid="replace-span-instruction-input"
            />
            <Helper>
              Re-translating {wordCount} {wordCount === 1 ? 'word' : 'words'}.
            </Helper>
            {directedBlockedText && (
              <Notice role="alert" data-testid="replace-span-directed-blocked">
                {directedBlockedText}
              </Notice>
            )}
            {directedFailure && !directedBlockedText && (
              <DirectedError
                role="alert"
                data-testid="replace-span-directed-error"
              >
                {DIRECTED_ERROR_TEXT}
              </DirectedError>
            )}
          </Section>
        )
      ) : (
        <Section>
          <SectionLabel>Your Translation</SectionLabel>
          <Textarea
            ref={textareaRef}
            value={value}
            onChange={e => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={3}
            aria-label="Replacement translation"
            data-testid="replace-span-input"
          />
          <Helper>
            Replacing {replacedCount} {replacedCount === 1 ? 'word' : 'words'}.
          </Helper>
        </Section>
      )}

      {/* PLAT-10266 (2026-07-28) — the stale-flag entry's SECOND way out: regenerate
          by machine, no instruction to write. Sits under the typed input because the
          two are alternatives for the same fragment ("type it, or let the machine
          redo it"). Only the stale-flag host wires `onRegenerate`; instruction mode
          is never reachable on that entry (it withholds `onConfirmDirected`) —
          guarded anyway. */}
      {onRegenerate && !instructionMode && (
        <RegenerateRow>
          <InlineTextAction
            type="button"
            onClick={onRegenerate}
            data-testid="replace-span-regenerate"
          >
            ↻ Regenerate with machine
          </InlineTextAction>
        </RegenerateRow>
      )}

      {/* PLAT-10266 (2026-07-28) — why the regenerate was refused. The op returns a
          typed failure and the workspace used to discard it, so a refused click looked
          like a dead button (proven live on the fragment "обычно": editor stayed open,
          zero backend calls, nothing said). Reuses the directed path's copy and both of
          its notice styles; only the placement is new, because this entry lives in
          manual mode. */}
      {onRegenerate && !instructionMode && regenerateFailureReason && (
        <>
          {regenerateBlockedText ? (
            <Notice role="alert" data-testid="replace-span-regenerate-blocked">
              {regenerateBlockedText}
            </Notice>
          ) : (
            <DirectedError
              role="alert"
              data-testid="replace-span-regenerate-error"
            >
              {DIRECTED_ERROR_TEXT}
            </DirectedError>
          )}
        </>
      )}

      {editedBy?.name && (
        <EditedMeta data-testid="replace-span-edited-meta">
          Last edited by {editedBy.name}
          {formatEditedAt(editedBy.at) && ` · ${formatEditedAt(editedBy.at)}`}
        </EditedMeta>
      )}

      <ButtonRow>
        <Spacer />
        <SecondaryButton
          type="button"
          onClick={onCancel}
          data-testid="replace-span-cancel"
        >
          Cancel
        </SecondaryButton>
        {!instructionMode && (
          <PrimaryButton
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            data-testid="replace-span-confirm"
          >
            Save
          </PrimaryButton>
        )}
        {instructionMode && !instructionModeBlockedMessage && (
          <PrimaryButton
            type="button"
            onClick={() => void handleConfirmDirected()}
            disabled={!canRetranslate}
            data-testid="replace-span-retranslate"
          >
            {isRetranslating ? 'Re-translating…' : 'Re-translate'}
          </PrimaryButton>
        )}
      </ButtonRow>
    </Wrapper>,
    document.body,
  );
};

/** Kept in sync with the `Wrapper`'s `width` declaration. */
const POPOVER_WIDTH = 360;
/** First-paint estimate only — the hook re-places against the real height. */
const POPOVER_ESTIMATED_HEIGHT = 360;

const Wrapper = styled.div.attrs(
  (props: { placement: AnchoredPopoverPlacement }) => props,
)`
  position: fixed;
  left: ${p => p.placement.left}px;
  top: ${p => p.placement.top}px;
  width: ${POPOVER_WIDTH}px;
  max-width: calc(100vw - 16px);
  /* Never spill past the viewport: the placement caps the height to the room
     on the side it chose, and the content scrolls inside instead. */
  max-height: ${p => p.placement.maxHeight}px;
  overflow-y: auto;
  /* Body-portaled, so it must clear the clip-preview modal stack on its own;
     inside the Translation Review workspace the lifted context-menu tier
     applies instead (see topLayerZ.ts). */
  z-index: var(
    ${TOP_LAYER_VARS.contextMenu},
    ${CLIP_PREVIEW_Z.translationEditorPopover}
  );

  background: var(--popover);
  color: var(--popover-foreground);
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  box-shadow: var(--elevation-toast);
  padding: 14px 16px;

  display: flex;
  flex-direction: column;
  gap: 12px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Helper = styled.div`
  font-size: 11px;
  line-height: 1.4;
  color: var(--text-muted);
`;

// "Last edited by … · …" attribution line for an existing change (PLAT-10444).
// Quiet meta, matching the Helper's muted weight.
const EditedMeta = styled.div`
  font-size: 11px;
  line-height: 1.4;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
`;

// PLAT-10266 (2026-07-28) — holds the stale-flag entry's "Regenerate with machine"
// action. Left-aligned under the typed input, matching the "Original Translation"
// row's Revert placement: the affordance sits next to what it acts on rather than in
// the footer, and it is a quiet text action, not a competing primary button.
const RegenerateRow = styled.div`
  display: flex;
  align-self: flex-start;
`;

// PLAT-10257 (A3) — the Type Replacement | Re-translate segmented control.
// Mirrors the `AudioModeSegmentedToggle` segments idiom (inline pill group on
// --surface-elevated) with a NEUTRAL active state: this picks an editing mode,
// not an action (green) or a visual/creative control (gold). Design-system
// tokens only; Title Case labels.
const ModeToggle = styled.div`
  display: inline-flex;
  align-self: flex-start;
  background: var(--surface-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  padding: 2px;
  gap: 2px;
`;

const ModeButton = styled.button<{ $active: boolean }>`
  border: 0;
  background: ${p => (p.$active ? 'var(--background)' : 'transparent')};
  color: ${p => (p.$active ? 'var(--foreground)' : 'var(--text-muted)')};
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  line-height: 1.4;
  cursor: pointer;
  transition:
    background-color 120ms ease,
    color 120ms ease;

  &:hover {
    color: var(--foreground);
  }
  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 1px;
  }
`;

// PLAT-10257 (A3) — minimal inline failure row for non-blocked directed
// failures (backend / author / missing source). Mirrors
// `DirectedRetranslateModal`'s ErrorRow idiom — the feature family's inline
// error; A6 replaces this with the polished notice. Existing tokens only.
const DirectedError = styled.div`
  font-size: 12px;
  line-height: 1.45;
  color: var(--destructive);
  background: color-mix(in srgb, var(--destructive) 12%, transparent);
  border: 1px solid var(--destructive);
  border-radius: var(--radius-input);
  padding: 8px 10px;
`;

// Every REFUSAL message in this editor: the warning panel's reason, and the
// inline "this mode can't do that" notices (instruction / directed / regenerate).
// Amber `--accent` attention tint — the technique `CaptionStalenessNotice` and
// `PaymentFailedBanner` use, since `--warning` is not a theme token. Distinct
// from `DirectedError`'s red, which means "it failed", not "it is not allowed".
// Sentence-case prose.
const Notice = styled.div`
  font-size: 12px;
  line-height: 1.45;
  color: var(--popover-foreground);
  background: var(--accent-tint-light);
  border: 1px solid var(--accent);
  border-radius: var(--radius-input);
  padding: 8px 10px;
`;

// The warning panel's heading row: attention glyph + the outcome as a title,
// mirroring `ConfirmModal`'s ModalHeading (icon top-aligned with the title's
// first line, neutral title text — the intent is carried by the tint and the
// buttons, not by colored heading text).
const WarningHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
`;

// Centers the glyph on the title's first text line.
const WarningIcon = styled.div`
  display: flex;
  align-items: center;
  height: var(--leading-text-sm);
  flex-shrink: 0;
`;

// One step up from the popover's 13px body — a heading inside a 360px anchored
// panel, not a page title (`--text-md` would out-shout the panel).
const WarningTitle = styled.div`
  font-size: var(--text-sm);
  line-height: var(--leading-text-sm);
  font-weight: var(--font-weight-semi-bold);
  color: var(--foreground);
`;

const Textarea = styled.textarea`
  background: var(--background);
  color: var(--foreground);
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  padding: 8px 10px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  line-height: 1.4;
  resize: vertical;
  min-height: 54px;
  outline: none;

  ${fieldFocusCss}
`;
